import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { MapPin, Briefcase, ChevronRight, Home } from "lucide-react";
import { useSeo, SITE_URL } from "../lib/useSeo";
import { useI18n } from "../lib/i18n";
import { CATEGORIES, CITIES } from "../lib/seoTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export { CATEGORIES, CITIES };

const matchesCategory = (job, cat) => {
  const hay = `${job.title || ""} ${job.description || ""} ${job.sector || ""} ${job.tags || ""}`.toLowerCase();
  return [cat.slug, cat.label_en, cat.label_fr, cat.en_kw, cat.fr_kw, ...(cat.aliases || [])]
    .some((k) => k && hay.includes(String(k).toLowerCase()));
};

const matchesCity = (job, city) => {
  const hay = (job.location || "").toLowerCase();
  return [city.slug, city.label, ...(city.aliases || [])]
    .some((k) => k && hay.includes(String(k).toLowerCase()));
};

function JobsList({ jobs, lang }) {
  if (!jobs.length) {
    return (
      <Card className="p-8 text-center text-slate-500" data-testid="landing-no-jobs">
        {lang === "fr"
          ? "Aucune offre active pour le moment. Revenez bientôt — de nouvelles offres sont publiées chaque semaine."
          : "No active openings right now. Check back soon — new roles are posted every week."}
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="landing-jobs-list">
      {jobs.map((j) => (
        <Card key={j.id} className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900">
                <Link to={`/jobs/${j.slug || j.id}`} className="hover:text-[#1A8FE3]" data-testid={`landing-job-${j.id}`}>
                  {j.title}
                </Link>
              </h3>
              <div className="text-sm text-slate-600 mt-1">{j.company_name}</div>
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                {j.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>}
                {j.contract_type && <Badge variant="outline" className="text-[10px]">{j.contract_type}</Badge>}
                {j.sector && <Badge variant="outline" className="text-[10px]">{j.sector}</Badge>}
              </div>
            </div>
            <Link to={`/jobs/${j.slug || j.id}`} className="text-[#1A8FE3] hover:text-[#157AC0] text-sm font-medium shrink-0 inline-flex items-center">
              {lang === "fr" ? "Voir" : "View"} <ChevronRight className="h-3 w-3 ml-0.5" />
            </Link>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Breadcrumbs({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4 flex items-center gap-1 flex-wrap" data-testid="landing-breadcrumbs">
      {trail.map((t, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <ChevronRight className="h-3 w-3" />}
          {t.href ? <Link to={t.href} className="hover:text-[#1A8FE3]">{t.label}</Link> : <span className="text-slate-700">{t.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

function useFilteredJobs(predicate) {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/jobs`).then((r) => {
      if (!alive) return;
      const all = (r.data || []).filter((j) => j.status === "active");
      setJobs(all.filter(predicate));
    }).catch(() => alive && setJobs([]));
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);
  return jobs;
}

/* Law-1 strict title template:
     [Primary EN Job Title/Sector] in Mauritius | [Primary FR Title/Sector] Île Maurice - Motravay
*/
const law1Title = (enKw, frKw) => `${enKw} Jobs in Mauritius | ${frKw} Île Maurice - Motravay`;
const law1TitleCity = (city) => `Jobs in ${city.label}, Mauritius | Emploi ${city.label} Île Maurice - Motravay`;

export function CategoryLandingPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const cat = useMemo(() => CATEGORIES.find((c) => c.slug === slug), [slug]);
  const jobs = useFilteredJobs((j) => cat ? matchesCategory(j, cat) : false);

  const title = cat ? law1Title(cat.en_kw, cat.fr_kw) : "Category — Motravay";
  // Tight 150-160 char bilingual description stitching EN + FR hooks
  const description = cat
    ? `${cat.en_kw} vacancies across Mauritius (Ebene, Port Louis, Grand Baie, Tamarin, Curepipe). ${cat.fr_kw} — offres d'emploi à Maurice. Postulez sur Motravay.`.slice(0, 160)
    : "Job categories on Motravay.";
  const canonical = `/jobs/category/${slug}`;
  const cravatte = cat ? (lang === "fr" ? cat.cravatte_fr : cat.cravatte_en) : "";

  const jsonLd = cat ? [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Motravay", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": lang === "fr" ? "Offres" : "Jobs", "item": `${SITE_URL}/jobs` },
        { "@type": "ListItem", "position": 3, "name": lang === "fr" ? cat.label_fr : cat.label_en, "item": `${SITE_URL}${canonical}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": title,
      "numberOfItems": jobs.length,
      "itemListElement": jobs.slice(0, 25).map((j, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "url": `${SITE_URL}/jobs/${j.slug || j.id}`,
        "name": j.title,
      })),
    },
  ] : null;

  useSeo({ title, description, canonical, jsonLd });

  if (!cat) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl">Category not found</h1>
        <Link to="/jobs/categories" className="text-[#1A8FE3] hover:underline mt-3 inline-block">See all categories</Link>
      </div>
    );
  }

  const localLabel = lang === "fr" ? cat.label_fr : cat.label_en;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="category-landing-page">
      <Breadcrumbs trail={[
        { label: <span className="inline-flex items-center gap-1"><Home className="h-3 w-3" />Motravay</span>, href: "/" },
        { label: lang === "fr" ? "Offres" : "Jobs", href: "/jobs" },
        { label: lang === "fr" ? "Catégories" : "Categories", href: "/jobs/categories" },
        { label: localLabel },
      ]} />
      <header className="mb-6">
        <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{lang === "fr" ? "Catégorie" : "Category"}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1" data-testid="landing-h1">
          {lang === "fr" ? `${cat.label_fr} — Emploi à Maurice` : `${cat.en_kw} Jobs in Mauritius`}
        </h1>
        <h2 className="mt-3 text-base text-slate-700 max-w-2xl leading-relaxed" data-testid="landing-cravatte">
          {cravatte}
        </h2>
        <p className="text-xs text-slate-500 mt-3">{jobs.length} {lang === "fr" ? "offre(s) ouverte(s)" : "open role(s)"}</p>
      </header>
      <JobsList jobs={jobs} lang={lang} />
    </div>
  );
}

export function CityLandingPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const city = useMemo(() => CITIES.find((c) => c.slug === slug), [slug]);
  const jobs = useFilteredJobs((j) => city ? matchesCity(j, city) : false);

  const title = city ? law1TitleCity(city) : "City — Motravay";
  const description = city
    ? `Jobs in ${city.label}, Mauritius. Hospitality, ICT, finance, BPO, retail, transport. Emploi à ${city.label} — postulez en ligne sur Motravay.`.slice(0, 160)
    : "Jobs by city on Motravay.";
  const canonical = `/jobs/location/${slug}`;

  const jsonLd = city ? [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Motravay", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": lang === "fr" ? "Offres" : "Jobs", "item": `${SITE_URL}/jobs` },
        { "@type": "ListItem", "position": 3, "name": city.label, "item": `${SITE_URL}${canonical}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": title,
      "numberOfItems": jobs.length,
      "itemListElement": jobs.slice(0, 25).map((j, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "url": `${SITE_URL}/jobs/${j.slug || j.id}`,
        "name": j.title,
      })),
    },
  ] : null;

  useSeo({ title, description, canonical, jsonLd });

  if (!city) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl">City not found</h1>
        <Link to="/jobs/locations" className="text-[#1A8FE3] hover:underline mt-3 inline-block">See all cities</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="city-landing-page">
      <Breadcrumbs trail={[
        { label: <span className="inline-flex items-center gap-1"><Home className="h-3 w-3" />Motravay</span>, href: "/" },
        { label: lang === "fr" ? "Offres" : "Jobs", href: "/jobs" },
        { label: lang === "fr" ? "Villes" : "Cities", href: "/jobs/locations" },
        { label: city.label },
      ]} />
      <header className="mb-6">
        <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{lang === "fr" ? "Lieu" : "Location"}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1" data-testid="landing-h1">
          {lang === "fr" ? `Emploi à ${city.label}, Maurice` : `Jobs in ${city.label}, Mauritius`}
        </h1>
        <h2 className="mt-3 text-base text-slate-700 max-w-2xl leading-relaxed">
          {lang === "fr"
            ? `Toutes les offres d'emploi à ${city.label} (${city.region}) — hôtellerie, IT, BPO, finance, vente, transport, industrie. Postulez en ligne avec ou sans CV.`
            : `Every active job in ${city.label} (${city.region}) — hospitality, ICT, BPO, finance, sales, transport, manufacturing. Apply online with or without a CV.`}
        </h2>
        <p className="text-xs text-slate-500 mt-3">{jobs.length} {lang === "fr" ? "offre(s) ouverte(s)" : "open role(s)"}</p>
      </header>
      <JobsList jobs={jobs} lang={lang} />
    </div>
  );
}

export function CategoriesIndexPage() {
  const { lang } = useI18n();
  const title = lang === "fr"
    ? "Toutes les catégories d'emploi à Maurice — Motravay"
    : "All Job Categories in Mauritius | Toutes les catégories d'emploi - Motravay";
  const description = lang === "fr"
    ? "Parcourez tous les secteurs d'activité à Maurice : IT, banque, hôtellerie, BPO, vente, transport, santé, éducation, économie bleue et plus encore."
    : "Browse every job sector in Mauritius — ICT, banking & finance, hospitality, BPO, sales, transport, healthcare, education, blue economy and more.";
  useSeo({ title, description, canonical: "/jobs/categories" });

  // Group by economic pillar so the page itself is a hierarchical SEO map
  const groups = useMemo(() => {
    return [
      { name: lang === "fr" ? "Pilier économique & cols blancs" : "Core Pillars & White-collar", slugs: ["it", "finance", "admin", "bpo"] },
      { name: lang === "fr" ? "Croissance, technique & cols bleus" : "High-growth, Technical & Blue-collar", slugs: ["hospitality", "construction", "sales", "transport", "manufacturing"] },
      { name: lang === "fr" ? "Spécialisé, santé & émergent" : "Specialised, Healthcare & Emerging", slugs: ["healthcare", "education", "blue-economy"] },
    ];
  }, [lang]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="categories-index-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{lang === "fr" ? "Catégories d'emploi à Maurice" : "Job categories in Mauritius"}</h1>
      <p className="mt-3 text-slate-600 max-w-3xl">{description}</p>

      {groups.map((g) => (
        <section key={g.name} className="mt-8">
          <h2 className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold mb-3">{g.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {g.slugs.map((slug) => {
              const c = CATEGORIES.find((x) => x.slug === slug);
              if (!c) return null;
              return (
                <Link
                  key={c.slug}
                  to={`/jobs/category/${c.slug}`}
                  data-testid={`cat-link-${c.slug}`}
                  className="group p-4 rounded-xl border border-slate-200 hover:border-[#1A8FE3] hover:shadow-md transition-all"
                >
                  <div className="inline-flex items-center gap-2 text-slate-900 font-semibold group-hover:text-[#1A8FE3]">
                    <Briefcase className="h-4 w-4" />
                    {lang === "fr" ? c.label_fr : c.label_en}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {lang === "fr" ? `${c.fr_kw} — emploi à Maurice` : `${c.en_kw} jobs in Mauritius`}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export function CitiesIndexPage() {
  const { lang } = useI18n();
  const title = lang === "fr"
    ? "Emplois par ville à Maurice — Motravay"
    : "Jobs by City in Mauritius | Emploi par ville - Motravay";
  const description = lang === "fr"
    ? "Trouvez un emploi par ville à Maurice : Ebène, Port-Louis, Quatre-Bornes, Curepipe, Grand Baie, Tamarin, Flacq, Mahébourg, Rodrigues et plus."
    : "Find a job by city in Mauritius — Ebène, Port-Louis, Quatre-Bornes, Curepipe, Grand Baie, Tamarin, Flacq, Mahébourg, Rodrigues and more.";
  useSeo({ title, description, canonical: "/jobs/locations" });

  // Group cities by region for stronger semantic structure
  const regions = useMemo(() => {
    const map = {};
    CITIES.forEach((c) => { if (!map[c.region]) map[c.region] = []; map[c.region].push(c); });
    return Object.entries(map);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="cities-index-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{lang === "fr" ? "Emplois par ville" : "Jobs by city"}</h1>
      <p className="mt-3 text-slate-600 max-w-3xl">{description}</p>
      {regions.map(([region, cities]) => (
        <section key={region} className="mt-8">
          <h2 className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold mb-3">{region}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/jobs/location/${c.slug}`}
                data-testid={`city-link-${c.slug}`}
                className="group p-4 rounded-xl border border-slate-200 hover:border-[#1A8FE3] hover:shadow-md transition-all"
              >
                <div className="inline-flex items-center gap-2 text-slate-900 font-semibold group-hover:text-[#1A8FE3]">
                  <MapPin className="h-4 w-4" />
                  {c.label}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {lang === "fr" ? `Offres d'emploi à ${c.label}` : `Jobs in ${c.label}, Mauritius`}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
